import { useMemo, useState, type ReactNode } from "react"

import { useSummarizerConfig } from "./useSummarizerConfig"
import { LabelsSidebar } from "./LabelsSidebar"
import { LabelDetail } from "./LabelDetail"
import { ImportExportBar } from "./ImportExportBar"
import {
  createEmptyLabel,
  duplicateLabel,
  ensureDocumentSubClassGroups,
  getDuplicateName,
  getInvalidTermCounts,
  validateLabelName,
  type SummarizerConfig,
  type SummarizerLabel,
} from "@/utils/summarizer-config"

import type { DocumentClassificationType } from "@/utils/types"

interface SummarizerEditorProps {
  storageKey: string
  classifications: DocumentClassificationType[]
  classificationsFailed?: boolean
  canWrite: boolean
  canDelete: boolean
  filterControls?: ReactNode
}

export function SummarizerEditor({
  storageKey,
  classifications,
  classificationsFailed = false,
  canWrite,
  canDelete,
  filterControls,
}: SummarizerEditorProps) {
  const classificationNames = useMemo(() => classifications.map(({ name }) => name), [classifications])
  const { config, update, saveError } = useSummarizerConfig(storageKey, classificationNames)
  const [selectedName, setSelectedName] = useState<string | null>(null)

  const names = useMemo(() => Object.keys(config).sort((a, b) => a.localeCompare(b)), [config])
  const invalidCounts = useMemo(() => getInvalidTermCounts(config), [config])

  const selected = selectedName == null ? undefined : config[selectedName]

  function addLabel(rawName: string) {
    if (!canWrite) return "You don't have permission to edit this configuration"

    const error = validateLabelName(rawName, names)
    if (error != null) return error

    const name = rawName.trim()
    update((current) => ({ ...current, [name]: createEmptyLabel(name, classificationNames) }))
    if (selectedName == null) setSelectedName(name)

    return null
  }

  function deleteLabel(name: string) {
    if (!canDelete) return

    update((current) => {
      const next = { ...current }
      delete next[name]
      return next
    })

    if (selectedName === name) setSelectedName(null)
  }

  function renameLabel(oldName: string, rawNewName: string) {
    if (!canWrite) return "You don't have permission to edit this configuration"

    const newName = rawNewName.trim()
    if (newName === oldName) return null

    const error = validateLabelName(newName, names, oldName)
    if (error != null) return error

    update((current) =>
      Object.fromEntries(
        Object.entries(current).map(([key, value]) =>
          key === oldName ? [newName, { ...value, label: newName }] : [key, value],
        ),
      ),
    )
    setSelectedName(newName)

    return null
  }

  function duplicateSelected(sourceName: string) {
    const source = config[sourceName]
    if (!canWrite || source == null) return

    const newName = getDuplicateName(source.label, names)

    update((current) => ({ ...current, [newName]: duplicateLabel(source, newName) }))
  }

  function importConfig(imported: SummarizerConfig) {
    if (!canWrite) return

    update(() => ensureDocumentSubClassGroups(imported, classificationNames))
    setSelectedName(null)
  }

  function clearConfig() {
    if (!canDelete) return

    update(() => ({}))
    setSelectedName(null)
  }

  function changeLabel(name: string, updater: (label: SummarizerLabel) => SummarizerLabel) {
    if (!canWrite) return

    update((current) => (current[name] == null ? current : { ...current, [name]: updater(current[name]) }))
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto lg:overflow-visible">
      <div className="flex shrink-0 flex-col items-stretch justify-between gap-3 lg:flex-row lg:flex-wrap lg:items-start">
        {filterControls}
        <ImportExportBar
          config={config}
          invalidCounts={invalidCounts}
          canImport={canWrite}
          canClear={canDelete}
          onImport={importConfig}
          onClear={clearConfig}
        />
      </div>
      {saveError && (
        <p role="alert" className="text-sm text-destructive">
          Could not save this configuration in this browser. Export it to avoid losing changes.
        </p>
      )}

      <div className="flex min-h-0 shrink-0 flex-col gap-4 lg:flex-1 lg:flex-row lg:gap-6">
        <LabelsSidebar
          names={names}
          selectedName={selectedName}
          invalidCounts={invalidCounts}
          canWrite={canWrite}
          canDelete={canDelete}
          onSelect={setSelectedName}
          onAdd={addLabel}
          onRename={renameLabel}
          onDuplicate={duplicateSelected}
          onDelete={deleteLabel}
        />

        <section className="flex min-h-80 min-w-0 flex-1 flex-col gap-4 rounded-lg border bg-background p-3 lg:min-h-0 lg:p-6">
          {selected == null ? (
            <p className="grid flex-1 place-items-center text-muted-foreground">
              Select or create a label to configure it
            </p>
          ) : (
            <LabelDetail
              key={selected.label}
              label={selected}
              classifications={classifications}
              classificationsFailed={classificationsFailed}
              canWrite={canWrite}
              canDelete={canDelete}
              onChange={(updater) => changeLabel(selected.label, updater)}
            />
          )}
        </section>
      </div>
    </div>
  )
}
